import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Snackbar,
  Alert,
  Grid,
  RadioGroup,
  FormControlLabel,
  Radio
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import userService from '../../services/userService';
import { useAuth } from '../../contexts/AuthContext';

interface UserFormData {
  username: string;
  email: string;
  password: string;
  role: string;
  first_name: string;
  last_name: string;
  phone_number: string;
  
  // 顧客特有の項目
  nickname: string;
  gender: 'male' | 'female' | 'unspecified';
  address: string;
  notes: string;
  
  // キャスト特有の項目
  nomination_fee: string;
  join_date: string;
  age: string;
  height: string;
  bust: string;
  cup: string;
  waist: string;
  hip: string;
  spec: string;
  special_nomination_fee: string;
}

const translateRole = (role: string): string => {
  const roleMap: { [key: string]: string } = {
    admin: '管理者',
    manager: 'マネージャー',
    staff: '内勤スタッフ',
    driver: 'ドライバー',
    cast: 'キャスト',
    customer: '顧客',
  };
  return roleMap[role.toLowerCase()] || role;
};

const UserCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [formData, setFormData] = useState<UserFormData>({
    username: '',
    email: '',
    password: '',
    role: '',
    first_name: '',
    last_name: '',
    phone_number: '',
    // 顧客・キャストの追加項目
    nickname: '',
    gender: 'unspecified',
    address: '',
    notes: '',
    nomination_fee: '',
    join_date: '',
    age: '',
    height: '',
    bust: '',
    cup: '',
    waist: '',
    hip: '',
    spec: '',
    special_nomination_fee: ''
  });
  const [roles, setRoles] = useState<string[]>([]);
  const [filteredRoles, setFilteredRoles] = useState<string[]>([]);
  const [error, setError] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  useEffect(() => {
    // 役割リストを定義
    const allRoles = ['admin', 'manager', 'staff', 'driver', 'cast', 'customer'];
    setRoles(allRoles);
  }, []);

  useEffect(() => {
    if (user && roles.length > 0) {
      if (user.role.toLowerCase() === 'admin') {
        setFilteredRoles(roles); // 管理者はすべての役割を表示
      } else {
        // 管理者以外は「管理者」と「マネージャー」を作成できない
        setFilteredRoles(roles.filter(role => !['admin', 'manager'].includes(role.toLowerCase())));
      }
    }
  }, [user, roles]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    setIsSubmitting(true);

    const dataToSubmit = {
      ...formData,
      // 文字列を数値に変換
      nomination_fee: formData.nomination_fee ? parseInt(formData.nomination_fee, 10) : undefined,
      special_nomination_fee: formData.special_nomination_fee ? parseInt(formData.special_nomination_fee, 10) : undefined,
      age: formData.age ? parseInt(formData.age, 10) : undefined,
      height: formData.height ? parseInt(formData.height, 10) : undefined,
      bust: formData.bust ? parseInt(formData.bust, 10) : undefined,
      waist: formData.waist ? parseInt(formData.waist, 10) : undefined,
      hip: formData.hip ? parseInt(formData.hip, 10) : undefined,
    };

    try {
      const response = await userService.createUser(dataToSubmit);
      
      setSnackbarMessage(`${response.username}が正常に作成されました。`);
      setSnackbarSeverity('success');
      setOpenSnackbar(true);

      // 成功したら一覧画面に戻る
      setTimeout(() => {
        navigate('/users');
      }, 1000);

    } catch (err: any) {
      const errorMessage = err.message || 'ユーザー作成中にエラーが発生しました';
      setError(errorMessage);
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name as string]: value
    }));
  };

  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name as string]: value
    }));
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom align="center">
            新規ユーザー作成
          </Typography>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6">基本情報</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
            <TextField
              label="ユーザー名"
              name="username"
              value={formData.username}
              onChange={handleChange}
              required
              fullWidth
            />
              </Grid>
              <Grid item xs={12} md={6}>
            <TextField
              label="メールアドレス"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
              fullWidth
            />
              </Grid>
              <Grid item xs={12} md={6}>
            <TextField
              label="パスワード"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleChange}
              required
              fullWidth
            />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
              <InputLabel id="role-label">役割</InputLabel>
              <Select
                labelId="role-label"
                name="role"
                value={formData.role}
                onChange={handleSelectChange}
                required
                label="役割"
              >
                    {filteredRoles.map(role => (
                      <MenuItem key={role} value={role.toUpperCase()}>
                        {translateRole(role)}
                      </MenuItem>
                    ))}
              </Select>
            </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="姓"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
            <TextField
              label="名"
              name="first_name"
              value={formData.first_name}
              onChange={handleChange}
              fullWidth
            />
              </Grid>
              <Grid item xs={12}>
            <TextField
              label="電話番号"
              name="phone_number"
              value={formData.phone_number}
              onChange={handleChange}
              fullWidth
                />
              </Grid>

              {/* Customer Fields */}
              {formData.role === 'CUSTOMER' && (
                <>
                  <Grid item xs={12}>
                    <Typography variant="h6" sx={{ mt: 2 }}>顧客情報</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField label="ニックネーム" name="nickname" value={formData.nickname} onChange={handleChange} fullWidth />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl component="fieldset">
                      <RadioGroup row name="gender" value={formData.gender} onChange={handleChange}>
                        <FormControlLabel value="male" control={<Radio />} label="男性" />
                        <FormControlLabel value="female" control={<Radio />} label="女性" />
                        <FormControlLabel value="unspecified" control={<Radio />} label="未指定" />
                      </RadioGroup>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField label="住所" name="address" value={formData.address} onChange={handleChange} fullWidth />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField label="備考" name="notes" value={formData.notes} onChange={handleChange} fullWidth multiline rows={3} />
                  </Grid>
                </>
              )}

              {/* Cast Fields */}
              {formData.role === 'CAST' && (
                <>
                  <Grid item xs={12}>
                    <Typography variant="h6" sx={{ mt: 2 }}>キャスト情報</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}><TextField label="本指名料" name="nomination_fee" value={formData.nomination_fee} onChange={handleChange} fullWidth /></Grid>
                  <Grid item xs={12} md={6}><TextField label="特別指名料" name="special_nomination_fee" value={formData.special_nomination_fee} onChange={handleChange} fullWidth /></Grid>
                  <Grid item xs={12} md={6}><TextField label="入店日" name="join_date" type="date" value={formData.join_date} onChange={handleChange} fullWidth InputLabelProps={{ shrink: true }} /></Grid>
                  <Grid item xs={12} md={6}><TextField label="年齢" name="age" value={formData.age} onChange={handleChange} fullWidth /></Grid>
                  <Grid item xs={12} md={4}><TextField label="身長" name="height" value={formData.height} onChange={handleChange} fullWidth /></Grid>
                  <Grid item xs={12} md={4}><TextField label="バスト" name="bust" value={formData.bust} onChange={handleChange} fullWidth /></Grid>
                  <Grid item xs={12} md={4}><TextField label="カップ" name="cup" value={formData.cup} onChange={handleChange} fullWidth /></Grid>
                  <Grid item xs={12} md={6}><TextField label="ウエスト" name="waist" value={formData.waist} onChange={handleChange} fullWidth /></Grid>
                  <Grid item xs={12} md={6}><TextField label="ヒップ" name="hip" value={formData.hip} onChange={handleChange} fullWidth /></Grid>
                  <Grid item xs={12}>
                    <TextField label="特記事項" name="spec" value={formData.spec} onChange={handleChange} fullWidth multiline rows={3} />
                  </Grid>
                </>
              )}

            </Grid>
            {error && (
              <Typography color="error" sx={{ mt: 2 }}>
                {error}
              </Typography>
            )}
            <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                disabled={isSubmitting}
              >
                作成
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                fullWidth
                onClick={() => navigate('/users')}
              >
                キャンセル
              </Button>
            </Box>
          </form>
        </Paper>
      </Box>
      <Snackbar 
        open={openSnackbar} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbarSeverity}
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default UserCreatePage;
